<script setup lang="ts">
  import { ref, computed } from "vue";
  import { useI18n } from "vue-i18n";
  import { Text, IconBasket } from "@ui-kit";

  const {
    image,
    name,
    article,
    color,
    link_created,
    visible_delete_button = true,
    color_date_link = "secondary",
  } = defineProps<{
    image: string;
    name: string;
    article: string;
    color: string;
    link_created: string;
    visible_delete_button?: boolean;
    color_date_link?: "secondary" | "error" | "warning";
  }>();
  const emit = defineEmits<{ "click:delete": [value: void] }>();

  const { locale } = useI18n();

  const visible_not_found_image = ref(false);

  const formatted_date = computed(() => {
    if (!link_created) return "";

    const date = new Date(link_created);
    const currentYear = new Date().getFullYear();
    const dateYear = date.getFullYear();
    const showYear = dateYear !== currentYear;

    const formatter = new Intl.DateTimeFormat(locale.value, {
      day: "numeric",
      month: "short",
      ...(showYear && { year: "numeric" }),
    });

    return formatter
      .format(date)
      .replace(/\.(?=\s|,)/, "")
      .replace(/\sг\.$/, "");
  });
</script>

<template>
  <div class="product-zone-group-item">
    <div class="product-zone-group-item__content">
      <div class="product-zone-group-item__image">
        <img v-if="!image || visible_not_found_image" :src="'/image2.png'" />
        <img v-else :src="'//' + image" @error="visible_not_found_image = true" />
      </div>
      <div class="product-zone-group-item__info">
        <Text font="body-medium-16" :wrap="false">{{ name }}</Text>
        <Text font="body-regular-14" color="secondary" :wrap="false">{{ article }}, {{ color }}</Text>
        <Text font="body-regular-12" :color="color_date_link">{{ formatted_date }}</Text>
      </div>
    </div>
    <div
      v-if="visible_delete_button"
      class="product-zone-group-item__delete"
      @click="emit('click:delete')"
      data-testid="product-zone-group-item-delete"
    >
      <IconBasket style="stroke: var(--sf-base-icon-error)" />
    </div>
  </div>
</template>

<style scoped>
  .product-zone-group-item {
    display: flex;
    border-radius: 12px;
    width: 100%;
    border: 1px solid var(--sf-base-border-secondary);
    background-color: var(--sf-base-background-white);
    overflow: hidden;
    flex-shrink: 0;
  }

  .product-zone-group-item__content {
    display: flex;
    flex: 1;
    padding: 12px;
    gap: 12px;
    align-items: center;
    overflow: hidden;
  }

  .product-zone-group-item__image {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    width: 48px;
    height: 62px;
    overflow: hidden;
    border-radius: 8px;

    & img {
      width: 100%;
    }
  }

  .product-zone-group-item__info {
    display: flex;
    flex-direction: column;
    gap: 4px;
    overflow: hidden;

    & :deep(.text) {
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .product-zone-group-item__delete {
    display: flex;
    padding: 28px 8px;
    background-color: var(--sf-base-background-error);
  }
</style>
